import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { convertToImageKitUrl } from '@lemonadefashion/imagekit';
import { elementSizePolicyPropType } from '../../element_sizing/size_policy_type';
import { sourceImageSizeSpecPropType } from '../../element_sizing/source_image_size_spec_type';
import { getImageKitSpecsForSourceImageSizeSpec } from '../../element_sizing/image_kit_specs';
import { getSizingCSS, getSizingCSSForImg } from '../../element_sizing/css';
import { getMobileNavbarHeightPx } from '../../utils/navbar_size';

export const getMediaUrl = (sourceMediaSizeSpec, mediaRoute, breakpoints, breakpointFlags, isVideo) => {
  const mediaSpecs = getImageKitSpecsForSourceImageSizeSpec(breakpoints, breakpointFlags, sourceMediaSizeSpec, isVideo);
  return convertToImageKitUrl(mediaRoute, { ...mediaSpecs });
};

export const getImageKitThumbnailUrl = (videoUrl) => {
  const url = new URL(videoUrl);
  // See: https://docs.imagekit.io/features/video-transformation/resize-crop-and-other-common-video-transformations
  //      #get-thumbnail-from-a-video
  url.pathname += '/ik-thumbnail.jpg';
  return url.toString();
};

const UIVideo = ({
  videoSizePolicy,
  setMarginAuto,
  fullScreen,
  showFullHeightVideo,
  borderRadius,
  bubbly,
  fallbackTransparent,
  sourceVideoSizeSpec,
  srcRoute,
  breakpoints,
  breakpointFlags,
  alt
}) => {
  const video = useRef(null);
  const stop = useRef(false);
  const videoUrl = getMediaUrl(sourceVideoSizeSpec, srcRoute, breakpoints, breakpointFlags, true);
  const thumbnailUrl = getImageKitThumbnailUrl(videoUrl);
  const mobileNavHeight = getMobileNavbarHeightPx();

  // This is an extremely ugly workaround specifically for Safari on Mac OS.
  // Safari on Mac OS loads the video successfully, and plays it, but it remains
  // completely invisible. To fix this, we learned that waiting for the play event,
  // and then explicitly resetting the src of the video to itself works.
  useEffect(() => {
    const videoRef = video.current;
    if (videoRef == null) return;
    const triggerFn = () => {
      if (stop.current) return;
      setTimeout(() => {
        stop.current = true;
        videoRef.src = videoUrl;
      }, 0);
    };
    videoRef.addEventListener('play', triggerFn);
    return () => videoRef.removeEventListener('play', triggerFn);
  }, [video, stop, videoUrl]);

  useEffect(() => {
    const videoRef = video.current;
    if (videoRef == null) return;
    const enforceMute = () => {
      if (!videoRef.muted) {
        videoRef.muted = true;
      }
    };
    videoRef.addEventListener('volumechange', enforceMute);
    return () => {
      videoRef.removeEventListener('volumechange', enforceMute);
    };
  }, [video]);

  return (
    <UIVideoRelativeContainer policy={videoSizePolicy} setMarginAuto={setMarginAuto} fullScreen={fullScreen}
      borderRadius={borderRadius} mobileNavHeight={mobileNavHeight} showFullHeightVideo={showFullHeightVideo}>
      <UIVideoFallback bubbly={bubbly} fallbackTransparent={fallbackTransparent} borderRadius={borderRadius} />
      <UIVideoElement policy={videoSizePolicy} autoPlay loop muted playsInline alt={alt}
        disableRemotePlayback preload='auto' ref={video} src={videoUrl} poster={thumbnailUrl}
        borderRadius={borderRadius} bubbly={bubbly} fullScreen={fullScreen} disablePictureInPicture />
    </UIVideoRelativeContainer>
  );
};

if (process.env.NODE_ENV !== 'production') {
  UIVideo.propTypes = {
    videoSizePolicy: elementSizePolicyPropType.isRequired,
    sourceVideoSizeSpec: sourceImageSizeSpecPropType.isRequired,
    breakpointFlags: PropTypes.arrayOf(PropTypes.bool.isRequired),
    breakpoints: PropTypes.arrayOf(PropTypes.number.isRequired),
    srcRoute: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    bubbly: PropTypes.bool,
    setMarginAuto: PropTypes.bool,
    fallbackTransparent: PropTypes.bool,
    borderRadius: PropTypes.string
  };
}

UIVideo.defaultProps = {
  bubbly: false,
  breakpointFlags: [],
  breakpoints: [],
  fallbackTransparent: false
};

const UIVideoRelativeContainer = styled.div`
  position: relative;
  ${props => props.fullScreen && 'width: 100%'};
  ${props => (props.fullScreen && !props.showFullHeightVideo)
    ? `height: calc(100vh - ${props.mobileNavHeight}px);`
    : ''};
  ${props => !props.fullScreen && getSizingCSS(props.policy)};
  ${props => props.setMarginAuto && 'margin: auto'};
  ${props => props.borderRadius && `border-radius: ${props.borderRadius}`};
  overflow: hidden;
`;

const UIVideoFallback = styled.div`
  position: absolute;
  top: 2px; bottom: 2px; right: 2px; left: 2px;
  z-index: 0;
  background: ${props => props.fallbackTransparent ? 'transparent' : '#f2f2f2'};
  ${props => props.borderRadius && `border-radius: ${props.borderRadius}`};
  ${props => props.bubbly && 'border-radius: 30px'};
`;

const UIVideoElement = styled.video`
  display: block;
  object-fit: cover;
  ${props => props.fullScreen && 'width: 100%'};
  ${props => props.fullScreen && 'height: 100%'};
  ${props => !props.fullScreen && getSizingCSSForImg(props.policy)};
  ${props => props.bubbly && 'border-radius: 30px'};
  position: relative;
  z-index: 3;
  ${props => props.borderRadius && `border-radius: ${props.borderRadius}`};
`;

export default UIVideo;
